/**
 * Cryptographic utilities for the poker platform
 */

export class CryptoUtils {
  /**
   * Generate a cryptographically secure random seed
   */
  static generateSeed(size: number = 32): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(size));
  }

  /**
   * Hash data using SHA-256
   */
  static async hash(data: string | Uint8Array): Promise<string> {
    const encoder = new TextEncoder();
    const dataBuffer = typeof data === 'string' ? encoder.encode(data) : new Uint8Array(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    return this.arrayBufferToHex(hashBuffer);
  }

  /**
   * Combine multiple seeds using XOR
   */
  static combineSeeds(seeds: Uint8Array[]): Uint8Array {
    if (seeds.length === 0) throw new Error('No seeds provided');
    
    const combined = new Uint8Array(seeds[0].length);
    for (let i = 0; i < combined.length; i++) {
      combined[i] = seeds.reduce((acc, seed) => acc ^ seed[i], 0);
    }
    return combined;
  }

  /**
   * Generate Ed25519 key pair for signing
   */
  static async generateKeyPair(): Promise<CryptoKeyPair> {
    return await crypto.subtle.generateKey(
      {
        name: 'Ed25519',
        namedCurve: 'Ed25519',
      },
      false,
      ['sign', 'verify']
    );
  }

  /**
   * Sign message with Ed25519 private key
   */
  static async sign(message: any, privateKey: CryptoKey): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(message));
    const signature = await crypto.subtle.sign('Ed25519', privateKey, new Uint8Array(data));
    return this.arrayBufferToBase64(signature);
  }

  /**
   * Verify Ed25519 signature
   */
  static async verify(
    message: any,
    signature: string,
    publicKey: CryptoKey
  ): Promise<boolean> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(message));
      const signatureBuffer = this.base64ToArrayBuffer(signature);
      
      return await crypto.subtle.verify(
        'Ed25519',
        publicKey,
        signatureBuffer,
        new Uint8Array(data)
      );
    } catch {
      return false;
    }
  }

  /**
   * Encrypt data using AES-256-GCM
   */
  static async encrypt(data: string, key: Uint8Array): Promise<string> {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    // Import key
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      new Uint8Array(key),
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );
    
    // Generate IV
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      dataBuffer
    );
    
    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedBuffer), iv.length);
    
    return this.arrayBufferToBase64(combined);
  }

  /**
   * Decrypt data using AES-256-GCM
   */
  static async decrypt(encryptedData: string, key: Uint8Array): Promise<string> {
    const combined = this.base64ToArrayBuffer(encryptedData);
    
    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const encryptedBuffer = combined.slice(12);
    
    // Import key
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      new Uint8Array(key),
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    
    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encryptedBuffer
    );
    
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  }

  /**
   * Generate commitment for commit-reveal scheme
   */
  static async createCommitment(value: any, nonce: Uint8Array): Promise<string> {
    const data = JSON.stringify(value) + this.arrayBufferToBase64(nonce);
    return await this.hash(data);
  }

  /**
   * Verify commitment in reveal phase
   */
  static async verifyCommitment(
    commitment: string,
    value: any,
    nonce: Uint8Array
  ): Promise<boolean> {
    const expectedCommitment = await this.createCommitment(value, nonce);
    return commitment === expectedCommitment;
  }

  /**
   * Generate proof of work
   */
  static async generateProofOfWork(
    challenge: string,
    difficulty: number
  ): Promise<{ solution: string; timestamp: number }> {
    const timestamp = Date.now();
    let nonce = 0;
    
    while (true) {
      const solution = `${challenge}:${timestamp}:${nonce}`;
      const hash = await this.hash(solution);
      
      if (this.checkProofOfWork(hash, difficulty)) {
        return { solution, timestamp };
      }
      
      nonce++;
      
      // Prevent infinite loop
      if (nonce > 1000000) {
        throw new Error('Proof of work generation failed');
      }
    }
  }

  /**
   * Verify proof of work
   */
  static checkProofOfWork(hash: string, difficulty: number): boolean {
    const target = '0'.repeat(difficulty);
    return hash.startsWith(target);
  }

  /**
   * Derive key from seed and position
   */
  static async deriveKey(seed: Uint8Array, position: number): Promise<Uint8Array> {
    const positionBytes = new Uint8Array(4);
    new DataView(positionBytes.buffer).setUint32(0, position, false);
    
    const combined = new Uint8Array(seed.length + positionBytes.length);
    combined.set(seed);
    combined.set(positionBytes, seed.length);
    
    const hashHex = await this.hash(combined);
    return this.hexToArrayBuffer(hashHex);
  }

  /**
   * Convert ArrayBuffer to Base64 string
   */
  static arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert Base64 string to ArrayBuffer
   */
  static base64ToArrayBuffer(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Convert ArrayBuffer to hex string
   */
  static arrayBufferToHex(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    return Array.from(bytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Convert hex string to ArrayBuffer
   */
  static hexToArrayBuffer(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
  }

  /**
   * Generate UUID v4
   */
  static generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (crypto.getRandomValues(new Uint8Array(1))[0] & 15) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Constant time string comparison (prevents timing attacks)
   */
  static constantTimeEqual(a: string, b: string): boolean {
    if (a.length !== b.length) return false;
    
    let result = 0;
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    
    return result === 0;
  }
}